import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Paperclip, FileText, Trash2, Upload } from "lucide-react";

const KIND_LABEL = { cheque_scan: "Cheque scan", delivery_note: "Delivery note", photo: "Photo", other: "Attachment" };
const token = () => localStorage.getItem("orgaviya_token") || "";

export function Attachments({ entityType, entityId, canUpload = true, kinds = ["delivery_note", "cheque_scan", "photo", "other"], compact = false }) {
  const { user } = useAuth();
  const [files, setFiles] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [kind, setKind] = useState(kinds[0]);
  const inputRef = useRef(null);

  const load = useCallback(() => api.get(`/files?entity_type=${entityType}&entity_id=${entityId}`).then((r) => setFiles(r.data)).catch(() => setFiles([])), [entityType, entityId]);
  useEffect(() => { if (entityId) load(); }, [entityId, load]);

  const upload = async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    setUploading(true);
    try {
      for (const f of list) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/files/upload?entity_type=${entityType}&entity_id=${entityId}&kind=${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`${list.length} file(s) attached`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };
  const remove = async (f) => {
    if (!window.confirm(`Remove ${f.original_filename}?`)) return;
    try {
      await api.delete(`/files/${f.id}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const url = (f) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${f.id}/download?auth=${token()}`;

  return (
    <div className={compact ? "" : "rounded-lg border border-border/60 p-3"} data-testid={`attachments-${entityType}-${entityId}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <p className="text-xs font-medium flex items-center gap-1.5"><Paperclip className="w-3.5 h-3.5" /> Attachments {files && <Badge variant="secondary" className="px-1.5 py-0 text-[10px]">{files.length}</Badge>}</p>
        {canUpload && (
          <div className="flex items-center gap-1.5">
            {kinds.length > 1 && (
              <select className="h-8 rounded-md border border-input bg-background px-2 text-xs" value={kind} onChange={(e) => setKind(e.target.value)} data-testid="attachment-kind-select">
                {kinds.map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
              </select>
            )}
            <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic,application/pdf" multiple className="hidden" onChange={upload} data-testid="attachment-file-input" />
            <Button size="sm" variant="outline" className="h-8" disabled={uploading} onClick={() => inputRef.current?.click()} data-testid="attachment-upload-button">
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Upload className="w-3.5 h-3.5 mr-1" /> Add</>}
            </Button>
          </div>
        )}
      </div>
      {!files ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /> : files.length === 0 ? <p className="text-xs text-muted-foreground">No files yet. Images (JPG/PNG/WEBP/HEIC) or PDF, max 10 MB.</p> : (
        <div className="flex flex-wrap gap-2">
          {files.map((f) => (
            <div key={f.id} className="group relative w-24" data-testid={`attachment-${f.id}`}>
              <a href={url(f)} target="_blank" rel="noreferrer" className="block w-24 h-24 rounded-md border border-border overflow-hidden bg-secondary/40 flex items-center justify-center">
                {f.content_type?.startsWith("image/") ? <img src={url(f)} alt={f.original_filename} className="w-full h-full object-cover" /> : <FileText className="w-8 h-8 text-muted-foreground" />}
              </a>
              <p className="text-[10px] text-muted-foreground truncate mt-1" title={f.original_filename}>{KIND_LABEL[f.kind] || "File"} · {f.original_filename}</p>
              {user?.role === "admin" && <button className="absolute top-1 right-1 hidden group-hover:flex w-6 h-6 rounded-full bg-destructive text-destructive-foreground items-center justify-center" onClick={() => remove(f)} data-testid={`delete-attachment-${f.id}`}><Trash2 className="w-3 h-3" /></button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
